import { SagaReturnType, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects'
import { PayloadAction } from '@reduxjs/toolkit'
import { KeycloakLoginOptions } from 'keycloak-js'

import auth, { AuthUser, UpdateAuthUserDto, LoginRequest, LoginResponse } from '@/core/classes/Auth'
import { axios } from '@/core/classes/HttpClient'

import {
  getAccountLink as getAccountLinkAction,
  getAccountLinkSuccess as getAccountLinkSuccessAction,
  getAccountLinkFailure as getAccountLinkFailureAction,
  getCalendsoPassword as getCalendsoPasswordAction,
  getCalendsoPasswordSuccess as getCalendsoPasswordSuccessAction,
  getCalendsoPasswordFailure as getCalendsoPasswordFailureAction,
  keycloakAuthSuccess as keycloakAuthSuccessAction,
  login as loginAction,
  logout as logoutAction,
  refreshToken as refreshTokenAction,
  setAccessToken as setAccessTokenAction,
  setAuthUser as setAuthUserAction,
  setAuthState as setAuthStateAction,
  setRefreshToken as setRefreshTokenAction,
  updateMyProfile as updateMyProfileAction,
  updateMyProfileSuccess as updateMyProfileSuccessAction,
  updateMyProfileFailure as updateMyProfileFailureAction,
  startTwilioVerify as startTwilioVerifyAction,
  startTwilioVerifySuccess as startTwilioVerifySuccessAction,
  startTwilioVerifyFailure as startTwilioVerifyFailureAction,
  checkTwilioVerify as checkTwilioVerifyAction,
  checkTwilioVerifySuccess as checkTwilioVerifySuccessAction,
  checkTwilioVerifyFailure as checkTwilioVerifyFailureAction,
  online,
  loginSilent as loginSilentAction,
  loginSilentSuccess as loginSilentSuccessAction,
  loginSilentFailure as loginSilentFailureAction,
  testPlugin as testPluginAction,
  testPluginSuccess as testPluginSuccessAction,
  testPluginFailure as testPluginFailureAction,
  selectAccessToken,
  selectAuthUser,
  getCalcomInfos as getCalcomInfosAction,
  getCalcomInfosSuccess as getCalcomInfosSuccessAction,
  getCalcomInfosFailure as getCalcomInfosFailureAction,
  getMyAvailability as getMyAvailabilityAction,
  getMyAvailabilitySuccess as getMyAvailabilitySuccessAction,
  getMyAvailabilityFailure as getMyAvailabilityFailureAction,
  updateMyAvailability as updateMyAvailabilityAction,
  updateMyAvailabilitySuccess as updateMyAvailabilitySuccessAction,
  updateMyAvailabilityFailure as updateMyAvailabilityFailureAction,
  getMyNotifications as getMyNotificationsAction,
  getMyNotificationsSuccess as getMyNotificationsSuccessAction,
  getMyNotificationsFailure as getMyNotificationsFailureAction,
  markNotificationAsRead as markNotificationAsReadAction,
  markNotificationAsReadSuccess as markNotificationAsReadSuccessAction,
  markNotificationAsReadFailure as markNotificationAsReadFailureAction,
  getMagicLink as getMagicLinkAction,
  getMagicLinkSuccess as getMagicLinkSuccessAction,
  getMagicLinkFailure as getMagicLinkFailureAction,
  checkStripeAccount as checkStripeAccountAction,
  checkStripeAccountSuccess as checkStripeAccountSuccessAction,
  checkStripeAccountFailure as checkStripeAccountFailureAction,
} from '@/redux/reducers/auth.reducer'

import { startBackgroundTasks } from './app.saga'

import { handleError } from '@/utils/errors'

import keycloak from '../../keycloak'
import staffModel, { PluginTest } from '@/core/models/staff.model'
import { Slot } from '@/core/models/schedule.model'
import { OFFLINE_AUTH_KEY, OFFLINE_USER_KEY, OFFLINE_TOKEN_KEY } from '@/constants/offline-auth'

// Cache authentication data for offline use
export function* cacheAuthDataForOffline(authUser: AuthUser) {
  try {
    const { token, refreshToken, idToken } = keycloak

    // Cache auth data with timestamp
    const authData = {
      lastOnlineAuth: new Date().toISOString(),
      userInfo: keycloak.userInfo,
      tokenInfo: {
        token,
        refreshToken,
        idToken
      }
    }

    // Store in localStorage for offline access
    localStorage.setItem(OFFLINE_AUTH_KEY, JSON.stringify(authData))
    localStorage.setItem(OFFLINE_USER_KEY, JSON.stringify(authUser))
    localStorage.setItem(OFFLINE_TOKEN_KEY, JSON.stringify({
      token,
      refreshToken,
      idToken
    }))


  } catch (error) {
    console.error('Failed to cache auth data for offline:', error)
  }
}

export function interceptAxios(action: PayloadAction<string | undefined>) {
  const accessToken = action.payload
  if (accessToken) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
  }
}

export function* getAccountLink(action: PayloadAction<{ refresh_url?: string; return_url?: string } | undefined>) {
  try {
    const accountLinkCreateParams = action.payload
    const url: string = yield call(auth.getAccountLink, accountLinkCreateParams)
    yield put(getAccountLinkSuccessAction(url))
  } catch (exception) {
    console.warn(exception)
    yield put(getAccountLinkFailureAction(handleError(exception)))
  }
}

function* getCalendsoPassword() {
  try {
    const authUser: AuthUser | null = yield select(selectAuthUser)
    if (authUser) {
      const password: string = yield call(auth.getCalendsoPassword, authUser._id)
      yield put(getCalendsoPasswordSuccessAction(password))
    }
  } catch (exception) {
    yield put(getCalendsoPasswordFailureAction(handleError(exception)))
  }
}

/**
 * On first connection, persist the user's notification timezone from their
 * browser if it isn't set yet, so server-sent notifications (emails/SMS) render
 * times in the user's own timezone. Best-effort: never blocks or breaks login.
 */
function* ensureNotificationTimezone(authUser: AuthUser) {
  try {
    const userInfo = authUser?.userInfo
    if (!userInfo || userInfo.notificationTimezone) return
    const tz =
      typeof Intl !== 'undefined' && Intl.DateTimeFormat ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined
    if (!tz) return
    yield call(auth.updateMyProfile, { userInfo: { notificationTimezone: tz } })
    yield put(setAuthUserAction({ ...authUser, userInfo: { ...userInfo, notificationTimezone: tz } } as AuthUser))
  } catch (exception) {
    console.warn('Failed to persist notification timezone', exception)
  }
}

function* keycloakAuthSuccess() {
  const { authenticated, refreshToken, token } = keycloak
  if (authenticated && token && refreshToken) {
    yield put(setAccessTokenAction(token))
    yield put(setRefreshTokenAction(refreshToken))
    yield put(setAuthStateAction(true))
    try {
      const authUser: AuthUser = yield call(auth.getMe)
      // order of these calls matters
      yield put(setAuthUserAction(authUser))
      yield put(online(authUser._id))
      yield call(startBackgroundTasks)
      yield call(cacheAuthDataForOffline, authUser)
      yield call(ensureNotificationTimezone, authUser)
    } catch (exception) {
      console.warn(exception)
      // TODO: handle getMe error
    }
  }
}

function* loginSilent(action: PayloadAction<LoginRequest>) {
  try {
    const token: LoginResponse = yield call(auth.login, action.payload)
    if (token.access_token) {
      yield put(setAccessTokenAction(token.access_token))
      yield put(setRefreshTokenAction(token.refresh_token))
      const isAuthenticated: boolean = yield keycloak.init({
        token: token.access_token,
        refreshToken: token.refresh_token,
        onLoad: 'check-sso',
        checkLoginIframe: false,
        silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
      })
      if (isAuthenticated) {
        loginSilentSuccessAction()
        try {
          const authUser: AuthUser = yield call(auth.getMe)
          yield put(setAuthUserAction(authUser))
          yield put(online(authUser._id))
          yield call(startBackgroundTasks)
          yield call(auth.resetStealthLogin, action.payload.username)
          yield call(cacheAuthDataForOffline, authUser)
          yield call(ensureNotificationTimezone, authUser)
        } catch (exception) {
          console.warn(exception)
          // TODO: handle getMe error
        }
      }
    }
  } catch (exception) {
    console.warn(exception)
    loginSilentFailureAction(exception)
    // TODO: handle keycloak.login error
  }
}

function* login(action: PayloadAction<KeycloakLoginOptions>) {
  try {
    yield call(keycloak.login, action.payload)
  } catch (exception) {
    console.warn(exception)
    // TODO: handle keycloak.login error
  }
}

function* logout() {
  try {
    // Close active time-tracking session before logout (fire-and-forget)
    try {
      const token: string | undefined = yield select(selectAccessToken)
      if (token) {
        yield call(axios.post, '/time-tracking/sessions/stop', null, {
          headers: { Authorization: `Bearer ${token}` },
        })
      }
    } catch {
      // Silent fail — proceed with logout regardless
    }

    // Clear offline cached data on logout
    localStorage.removeItem(OFFLINE_AUTH_KEY)
    localStorage.removeItem(OFFLINE_USER_KEY)
    localStorage.removeItem(OFFLINE_TOKEN_KEY)


    yield call(keycloak.logout)
  } catch (exception) {
    console.warn(exception)
    // TODO: handle keycloak.logout error
  }
}

function* refreshToken() {
  const { refreshToken, token } = keycloak
  if (token && refreshToken) {
    yield put(setAccessTokenAction(token))
    yield put(setRefreshTokenAction(refreshToken))
  }
}

export function* updateMyProfile(action: PayloadAction<UpdateAuthUserDto>) {
  try {
    const updateAuthUserDto = action.payload
    const updatedAuthUser: AuthUser = yield call(auth.updateMyProfile, updateAuthUserDto)
    yield put(updateMyProfileSuccessAction(updatedAuthUser))
    // Update cached offline data with new profile
    yield call(cacheAuthDataForOffline, updatedAuthUser)
  } catch (exception) {
    console.warn(exception)
    yield put(updateMyProfileFailureAction(handleError(exception)))
  }
}

export function* startTwilioVerify(action: PayloadAction<{ to: string; channel: 'email' | 'sms' }>) {
  try {
    const { to, channel } = action.payload
    yield call(auth.startTwilioVerify, to, channel)
    yield put(startTwilioVerifySuccessAction())
  } catch (exception) {
    console.warn(exception)
    yield put(startTwilioVerifyFailureAction(handleError(exception)))
  }
}

export function* checkTwilioVerify(action: PayloadAction<{ code: string; to: string }>) {
  try {
    const { to, code } = action.payload
    yield call(auth.checkTwilioVerify, to, code)
    yield put(checkTwilioVerifySuccessAction())
  } catch (exception) {
    console.warn(exception)
    yield put(checkTwilioVerifyFailureAction(handleError(exception)))
  }
}

function* testPlugin() {
  try {
    const plugins: [PluginTest] = yield call(staffModel.testPlugin)
    yield put(testPluginSuccessAction(plugins))
  } catch (exception) {
    console.warn(exception)
    yield put(testPluginFailureAction(handleError(exception)))
  }
}

function* getCalcom() {
  try {
    const calcomInfos: { link: string; username: string; password: string } = yield call(auth.getCalcom)
    yield put(getCalcomInfosSuccessAction(calcomInfos))
  } catch (exception) {
    console.warn(exception)
    yield put(getCalcomInfosFailureAction(handleError(exception)))
  }
}

function* getMyAvailability() {
  try {
    const availability: SagaReturnType<typeof auth.getMyAvailibility> = yield call(auth.getMyAvailibility)
    yield put(getMyAvailabilitySuccessAction(availability))
  } catch (exception) {
    console.warn(exception)
    yield put(getMyAvailabilityFailureAction(handleError(exception)))
  }
}

function* updateMyAvailability(action: PayloadAction<Slot[]>) {
  try {
    const availability: SagaReturnType<typeof auth.updateMyAvailibility> = yield call(auth.updateMyAvailibility, action.payload)
    yield put(updateMyAvailabilitySuccessAction(availability))
  } catch (exception) {
    console.warn(exception)
    yield put(updateMyAvailabilityFailureAction(handleError(exception)))
  }
}

function* getMyEvents(action: PayloadAction<ReturnType<typeof getMyNotificationsAction>['payload']>) {
  try {
    const events: SagaReturnType<typeof auth.getMyEvents> = yield call(auth.getMyEvents, action.payload.limit, action.payload.skip)
    yield put(getMyNotificationsSuccessAction(events))
  } catch (exception) {
    console.warn(exception)
    yield put(getMyNotificationsFailureAction(handleError(exception)))
  }
}

function* markNotificationAsRead(action: PayloadAction<ReturnType<typeof markNotificationAsReadAction>['payload']>) {
  try {
    const events: SagaReturnType<typeof auth.readMyEvents> = yield call(auth.readMyEvents, action.payload.eventId)
    yield put(markNotificationAsReadSuccessAction(events))
  } catch (exception) {
    console.warn(exception)
    yield put(markNotificationAsReadFailureAction(handleError(exception)))
  }
}

function* getMagicLink(action: PayloadAction<ReturnType<typeof getMagicLinkAction>['payload']>) {
  try {
    const { url }: SagaReturnType<typeof auth.getMagicLink> = yield call(auth.getMagicLink, action.payload)
    yield put(getMagicLinkSuccessAction(url))
  } catch (exception) {
    yield put(getMagicLinkFailureAction(handleError(exception)))
  }
}

function* checkStripeAccount(action: PayloadAction<{ staffId: string }>) {
  try {
    const staff: SagaReturnType<typeof staffModel.getStripeAccount> = yield call(staffModel.getStripeAccount)
    console.log(staff)
    if (staff.staff.stripe) {
      yield put(checkStripeAccountSuccessAction({
        staff: staff.staff
      }))
    } else {
      yield put(checkStripeAccountFailureAction('Stripe account not found'))
    }
  } catch (exception) {
    yield put(checkStripeAccountFailureAction(handleError(exception)))
  }
}

const sagas = [
  takeLatest(getAccountLinkAction.type, getAccountLink),
  takeLatest(getCalendsoPasswordAction.type, getCalendsoPassword),
  takeLatest(keycloakAuthSuccessAction.type, keycloakAuthSuccess),
  takeLatest(loginAction.type, login),
  takeLatest(logoutAction.type, logout),
  takeLatest(refreshTokenAction.type, refreshToken),
  takeLatest(setAccessTokenAction.type, interceptAxios),
  takeLatest(updateMyProfileAction.type, updateMyProfile),
  takeLatest(startTwilioVerifyAction.type, startTwilioVerify),
  takeLatest(checkTwilioVerifyAction.type, checkTwilioVerify),
  takeLatest(loginSilentAction.type, loginSilent),
  takeLatest(testPluginAction.type, testPlugin),
  takeLatest(getCalcomInfosAction.type, getCalcom),
  takeLatest(getMyAvailabilityAction.type, getMyAvailability),
  takeLatest(updateMyAvailabilityAction.type, updateMyAvailability),
  takeLatest(getMyNotificationsAction.type, getMyEvents),
  takeEvery(markNotificationAsReadAction.type, markNotificationAsRead),
  takeLatest(getMagicLinkAction.type, getMagicLink),
  takeLatest(checkStripeAccountAction.type, checkStripeAccount),

]

export default sagas
